"use client";

import { CheckCircle2, LoaderCircle } from "lucide-react";
import { useTranslations } from "next-intl";
import { useRef, useState, type FormEvent } from "react";

import { InstitutionApiError, institutionRequest } from "@/lib/institutions-api";

type Field = "institutionName" | "segment" | "contactName" | "email" | "phone";
type FieldErrors = Partial<Record<Field, string>>;
const segments = ["COLLEGE", "UNIVERSITY", "HIGH_SCHOOL", "OTHER"] as const;

export function InstitutionDemoForm({ title, description }: { title?: string; description?: string } = {}) {
  const t = useTranslations("InstitutionalTransition");
  const [state, setState] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [errors, setErrors] = useState<FieldErrors>({});
  const [requestError, setRequestError] = useState("demoRetryError");
  const [sentEmail, setSentEmail] = useState("");
  const submitting = useRef(false);
  const focusAfterRender = useRef<Field | null>(null);

  function fieldError(name: Field) {
    return errors[name] ? <p id={`demo-${name}-error`} className="mt-2 text-sm font-normal leading-5 text-danger">{t(errors[name]!)}</p> : null;
  }

  function inputProps(name: Field) {
    return {
      id: `demo-${name}`,
      name,
      "aria-invalid": errors[name] ? true : undefined,
      "aria-describedby": errors[name] ? `demo-${name}-error` : undefined,
      onChange: () => {
        if (errors[name]) setErrors(current => ({ ...current, [name]: undefined }));
      },
      className: `mt-2 min-h-12 w-full rounded-xl border bg-background px-4 text-foreground outline-none focus:border-accent focus:ring-2 focus:ring-accent/20 ${errors[name] ? "border-danger" : "border-border"}`,
    };
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting.current) return;
    const element = event.currentTarget;
    const form = new FormData(element);
    const values = Object.fromEntries(["institutionName", "segment", "contactName", "email", "phone"].map(name => [name, String(form.get(name) ?? "").trim()])) as Record<Field, string>;
    const nextErrors: FieldErrors = {};
    if (!values.institutionName) nextErrors.institutionName = "demoInstitutionRequired";
    if (!segments.includes(values.segment as typeof segments[number])) nextErrors.segment = "demoSegmentRequired";
    if (!values.contactName) nextErrors.contactName = "demoContactRequired";
    if (!values.email) nextErrors.email = "demoEmailRequired";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) nextErrors.email = "demoEmailInvalid";
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      setState("idle");
      (element.elements.namedItem(Object.keys(nextErrors)[0]) as HTMLElement | null)?.focus();
      return;
    }
    submitting.current = true;
    setState("sending");
    try {
      await institutionRequest("/demo", { ...values, phone: values.phone || null });
      setSentEmail(values.email);
      setState("sent");
    } catch (error) {
      if (error instanceof InstitutionApiError && error.code === "EMAIL_INVALID") {
        setErrors({ email: "demoEmailInvalid" });
        focusAfterRender.current = "email";
        setState("idle");
      } else {
        setRequestError(error instanceof InstitutionApiError && error.status === 429 ? "demoRateLimitError" : "demoRetryError");
        setState("error");
      }
    } finally {
      submitting.current = false;
    }
  }

  return <section id="demonstracao" aria-labelledby="demo-title" className="scroll-mt-8 rounded-3xl border border-border bg-surface p-6 sm:p-9">
    <h2 id="demo-title" className="text-2xl font-semibold tracking-tight">{title ?? t("demoTitle")}</h2>
    <p className="mt-3 text-sm leading-7 text-muted">{description ?? t("demoBody")}</p>
    {state === "sent" ? <div className="mt-6 rounded-2xl border border-accent/20 bg-accent/5 p-5 sm:p-6">
      <div role="status" tabIndex={-1} ref={element => element?.focus()} className="rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-accent">
        <CheckCircle2 aria-hidden="true" className="mb-3 text-accent" size={28} />
        <p className="font-medium leading-7">{t("demoSent")}</p>
        <p className="mt-2 break-words text-sm leading-6 text-muted">{t("demoSentContact", { email: sentEmail })}</p>
      </div>
      <button type="button" onClick={() => { focusAfterRender.current = "institutionName"; setState("idle"); setErrors({}); }} className="mt-4 min-h-11 rounded-lg px-2 text-sm font-semibold text-accent underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-accent">{t("demoAnother")}</button>
    </div> : <form noValidate onSubmit={submit} ref={element => {
      if (element && focusAfterRender.current) {
        const field = focusAfterRender.current;
        focusAfterRender.current = null;
        (element.elements.namedItem(field) as HTMLElement | null)?.focus();
      }
    }} aria-busy={state === "sending"} aria-describedby="demo-required" className="mt-6">
      <p id="demo-required" className="mb-5 text-xs leading-6 text-muted">{t("demoRequired")}</p>
      <fieldset disabled={state === "sending"} className="grid min-w-0 gap-5 sm:grid-cols-2">
        <div className="min-w-0 text-sm font-medium"><label htmlFor="demo-institutionName">{t("institutionName")}<span aria-hidden="true"> *</span></label><input {...inputProps("institutionName")} autoComplete="organization" required maxLength={200}/>{fieldError("institutionName")}</div>
        <div className="min-w-0 text-sm font-medium"><label htmlFor="demo-segment">{t("segment")}<span aria-hidden="true"> *</span></label><select {...inputProps("segment")} required defaultValue="COLLEGE">{segments.map(value => <option key={value} value={value}>{t(`segments.${value}`)}</option>)}</select>{fieldError("segment")}</div>
        <div className="min-w-0 text-sm font-medium"><label htmlFor="demo-contactName">{t("contactName")}<span aria-hidden="true"> *</span></label><input {...inputProps("contactName")} autoComplete="name" required maxLength={200}/>{fieldError("contactName")}</div>
        <div className="min-w-0 text-sm font-medium"><label htmlFor="demo-email">{t("email")}<span aria-hidden="true"> *</span></label><input {...inputProps("email")} type="email" autoComplete="email" required maxLength={320}/>{fieldError("email")}</div>
        <div className="min-w-0 text-sm font-medium"><label htmlFor="demo-phone">{t("phoneOptional")}</label><input {...inputProps("phone")} type="tel" autoComplete="tel" maxLength={40}/></div>
        <div className="flex items-end"><button type="submit" className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-xl bg-accent px-5 font-semibold text-accent-foreground outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface disabled:opacity-60">{state === "sending" && <LoaderCircle aria-hidden="true" size={18} className="animate-spin"/>}{t(state === "sending" ? "sending" : state === "error" ? "demoRetry" : "requestDemo")}</button></div>
      </fieldset>
      {state === "error" && <p role="alert" className="mt-5 text-sm leading-6 text-danger">{t(requestError)}</p>}
    </form>}
  </section>;
}
